if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();

        this.touchStartX = 0;
        this.touchStartY = 0;
        this.touchEndX = 0;
        this.touchEndY = 0;

        this.handleKeydown = this.handleKeydown.bind(this);
        this.handleTouchStart = this.handleTouchStart.bind(this);
        this.handleTouchEnd = this.handleTouchEnd.bind(this);
      }

      connectedCallback() {
        if (super.connectedCallback) {
          super.connectedCallback();
        }

        this.createNavigation();
      }

      hide() {
        document.removeEventListener('keydown', this.handleKeydown);

        const container = this.querySelector('[role="document"]');

        if (container) {
          container.removeEventListener('touchstart', this.handleTouchStart);
          container.removeEventListener('touchend', this.handleTouchEnd);
        }

        super.hide();
      }

      show(opener) {
        super.show(opener);

        this.createNavigation();
        this.showActiveMedia();

        document.addEventListener('keydown', this.handleKeydown);

        const container = this.querySelector('[role="document"]');

        if (container) {
          container.addEventListener('touchstart', this.handleTouchStart, {
            passive: true,
          });

          container.addEventListener('touchend', this.handleTouchEnd, {
            passive: true,
          });
        }
      }

      getMediaItems() {
        const container = this.querySelector('[role="document"]');

        if (!container) return [];

        return Array.from(container.children).filter((element) =>
          element.hasAttribute('data-media-id')
        );
      }

      getActiveIndex() {
        const mediaItems = this.getMediaItems();

        return mediaItems.findIndex((element) =>
          element.classList.contains('active')
        );
      }

      showActiveMedia() {
        if (!this.openedBy) return;

        const mediaId = this.openedBy.getAttribute('data-media-id');

        this.showMediaById(mediaId);
      }

      showMediaById(mediaId) {
        const mediaItems = this.getMediaItems();

        if (!mediaItems.length) return;

        mediaItems.forEach((element) => {
          element.classList.remove('active');
        });

        const activeMedia = mediaItems.find(
          (element) => element.getAttribute('data-media-id') === mediaId
        );

        if (!activeMedia) return;

        activeMedia.classList.add('active');

        const container = this.querySelector('[role="document"]');

        if (container) {
          container.scrollTop = 0;
          container.scrollLeft = 0;
        }

        this.loadMedia(activeMedia);
        this.updateNavigation();
      }

      showMediaByIndex(index) {
        const mediaItems = this.getMediaItems();

        if (!mediaItems.length) return;

        if (index < 0) {
          index = mediaItems.length - 1;
        }

        if (index >= mediaItems.length) {
          index = 0;
        }

        mediaItems.forEach((element) => {
          element.classList.remove('active');
        });

        const activeMedia = mediaItems[index];

        activeMedia.classList.add('active');

        const container = this.querySelector('[role="document"]');

        if (container) {
          container.scrollTop = 0;
          container.scrollLeft = 0;
        }

        this.loadMedia(activeMedia);
        this.updateNavigation();
      }

      showPreviousMedia() {
        const activeIndex = this.getActiveIndex();

        if (activeIndex === -1) return;

        this.showMediaByIndex(activeIndex - 1);
      }

      showNextMedia() {
        const activeIndex = this.getActiveIndex();

        if (activeIndex === -1) return;

        this.showMediaByIndex(activeIndex + 1);
      }

      loadMedia(activeMedia) {
        const activeMediaTemplate = activeMedia.querySelector('template');

        const activeMediaContent = activeMediaTemplate
          ? activeMediaTemplate.content
          : null;

        if (
          activeMedia.nodeName === 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        ) {
          activeMedia.loadContent();
        }
      }

      createNavigation() {
        const dialog = this.querySelector('.product-media-modal__dialog');

        if (!dialog) return;

        if (!dialog.querySelector('.sqmize-media-modal__previous')) {
          const previousButton = document.createElement('button');

          previousButton.type = 'button';
          previousButton.className =
            'sqmize-media-modal__nav sqmize-media-modal__previous';

          previousButton.setAttribute(
            'aria-label',
            'Vorige productafbeelding'
          );

          previousButton.innerHTML =
            '<span aria-hidden="true">‹</span>';

          /*
           * Shopify sluit een media-modal standaard bij pointerup
           * buiten deferred-media/product-model.
           * Daarom stoppen we pointerup op onze eigen navigatieknop.
           */
          previousButton.addEventListener('pointerup', (event) => {
            event.stopPropagation();
          });

          previousButton.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();

            this.showPreviousMedia();
          });

          dialog.appendChild(previousButton);
        }

        if (!dialog.querySelector('.sqmize-media-modal__next')) {
          const nextButton = document.createElement('button');

          nextButton.type = 'button';
          nextButton.className =
            'sqmize-media-modal__nav sqmize-media-modal__next';

          nextButton.setAttribute(
            'aria-label',
            'Volgende productafbeelding'
          );

          nextButton.innerHTML =
            '<span aria-hidden="true">›</span>';

          /*
           * Voorkom dat ModalDialog deze muisklik interpreteert
           * als een klik buiten de media.
           */
          nextButton.addEventListener('pointerup', (event) => {
            event.stopPropagation();
          });

          nextButton.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();

            this.showNextMedia();
          });

          dialog.appendChild(nextButton);
        }

        this.updateNavigation();
      }

      updateNavigation() {
        const mediaItems = this.getMediaItems();

        const previousButton = this.querySelector(
          '.sqmize-media-modal__previous'
        );

        const nextButton = this.querySelector(
          '.sqmize-media-modal__next'
        );

        const showButtons = mediaItems.length > 1;

        if (previousButton) {
          previousButton.hidden = !showButtons;
        }

        if (nextButton) {
          nextButton.hidden = !showButtons;
        }
      }

      handleKeydown(event) {
        if (!this.hasAttribute('open')) return;

        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.showPreviousMedia();
        }

        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.showNextMedia();
        }
      }

      handleTouchStart(event) {
        if (event.touches.length !== 1) return;

        this.touchStartX = event.touches[0].clientX;
        this.touchStartY = event.touches[0].clientY;
      }

      handleTouchEnd(event) {
        if (event.changedTouches.length !== 1) return;

        this.touchEndX = event.changedTouches[0].clientX;
        this.touchEndY = event.changedTouches[0].clientY;

        const differenceX = this.touchEndX - this.touchStartX;
        const differenceY = this.touchEndY - this.touchStartY;

        /*
         * Alleen horizontale swipe met één vinger.
         * Pinch-to-zoom met twee vingers blijft hierdoor ongemoeid.
         */
        if (
          Math.abs(differenceX) < 50 ||
          Math.abs(differenceX) <= Math.abs(differenceY)
        ) {
          return;
        }

        if (differenceX > 0) {
          this.showPreviousMedia();
        } else {
          this.showNextMedia();
        }
      }
    }
  );
}